import React, { useState, useEffect } from 'react';
import api from '../api';
import { Bot, CheckCircle, AlertTriangle, RefreshCw, Check, MessageSquare, Sparkles } from 'lucide-react';
import AiDisclaimerCard from './AiDisclaimerCard';

const AIPlacementRecommendationCard = ({ application, onUpdateApplication, onApprove, onRemark }) => {
  const [loadingAiRec, setLoadingAiRec] = useState(false);
  const [aiRecError, setAiRecError] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const requireDisclaimer = (action) => {
    if (sessionStorage.getItem('ai_disclaimer_agreed')) {
      action();
    } else {
      setPendingAction(() => action);
      setShowDisclaimer(true);
    }
  };

  useEffect(() => {
    if (application && application.id && !application.ai_recommendation && !loadingAiRec) {
      requireDisclaimer(() => _runGenerateRecommendation());
    }
  }, [application?.id, application?.ai_recommendation]);

  const handleGenerateRecommendation = () => {
    requireDisclaimer(() => _runGenerateRecommendation());
  };

  const _runGenerateRecommendation = async () => {
    if (!application || !application.id) return;
    setLoadingAiRec(true);
    setAiRecError(null);
    try {
      const res = await api.post(`/tpo/applications/${application.id}/ai-recommendation`);
      if (onUpdateApplication && res.data) {
        onUpdateApplication({ ai_recommendation: typeof res.data === 'string' ? res.data : JSON.stringify(res.data) });
      }
    } catch (err) {
      console.error("AI Recommendation Error:", err);
      setAiRecError("Failed to generate AI recommendation. Please try again.");
    } finally {
      setLoadingAiRec(false);
    }
  };

  if (!application) return null;

  let recData = null;
  if (application.ai_recommendation) {
    try {
      recData = typeof application.ai_recommendation === 'string' ? JSON.parse(application.ai_recommendation) : application.ai_recommendation;
    } catch (e) {
      recData = { recommendation: 'Under Review', explanation: application.ai_recommendation };
    }
  }

  const isApproved = recData && recData.recommendation?.toLowerCase().includes('approve');

  return (
    <div style={{
      padding: '24px',
      background: recData ? (
        isApproved
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.03))'
          : 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(217, 119, 6, 0.03))'
      ) : 'linear-gradient(135deg, rgba(59, 130, 246, 0.06), rgba(139, 92, 246, 0.03))',
      border: `1.5px solid ${
        recData ? (
          isApproved ? '#34d399' : '#fbbf24'
        ) : '#bfdbfe'
      }`,
      borderRadius: '14px',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '12px',
            background: recData ? (
              isApproved ? '#10b981' : '#f59e0b'
            ) : '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            boxShadow: '0 3px 10px rgba(0,0,0,0.15)'
          }}>
            <Bot size={22} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--faculty-text, #111827)' }}>
              AI Placement Recommendation
            </h4>
            <span style={{ fontSize: '0.82rem', color: 'var(--faculty-muted, #6b7280)' }}>
              Evaluated for role: <strong style={{ color: 'var(--faculty-accent, #3b82f6)' }}>{application.job_name || 'Software Engineer'}</strong>
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {recData && (
            <span style={{
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '0.82rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: isApproved ? '#dcfce7' : '#fef3c7',
              color: isApproved ? '#15803d' : '#b45309',
              border: `1px solid ${isApproved ? '#86efac' : '#fde68a'}`
            }}>
              {isApproved ? <CheckCircle size={15} /> : <AlertTriangle size={15} />}
              {isApproved ? 'Approved' : 'Mark with Remark'}
            </span>
          )}
          <button
            onClick={handleGenerateRecommendation}
            disabled={loadingAiRec}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#fff',
              color: '#334155',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: loadingAiRec ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <RefreshCw size={14} className={loadingAiRec ? 'spin-animation' : ''} />
            {loadingAiRec ? 'Evaluating...' : recData ? 'Re-Analyze' : 'Generate AI Decision'}
          </button>
        </div>
      </div>

      {loadingAiRec ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#3b82f6', fontSize: '0.92rem', fontWeight: 600, background: 'rgba(255,255,255,0.7)', borderRadius: '10px' }}>
          AI is cross-referencing candidate skills, GitHub repositories, and resume against requirements...
        </div>
      ) : aiRecError ? (
        <div style={{ padding: '14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#dc2626', fontSize: '0.88rem' }}>
          {aiRecError}
        </div>
      ) : recData ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ fontSize: '0.92rem', color: 'var(--faculty-text, #1e293b)', lineHeight: 1.6, background: '#fff', padding: '16px', borderRadius: '10px', border: '1px solid var(--faculty-border, #e2e8f0)' }}>
            <strong style={{ color: isApproved ? '#059669' : '#d97706' }}>AI Explanation:</strong> {recData.explanation}
          </div>

          {Array.isArray(recData.strengths) && recData.strengths.length > 0 && (
            <div>
              <strong style={{ fontSize: '0.86rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <CheckCircle size={15} /> Key Profile Strengths & Alignment:
              </strong>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {recData.strengths.map((str, idx) => (
                  <div key={idx} style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '8px 12px', borderRadius: '8px', fontSize: '0.84rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Check size={14} style={{ color: '#10b981', flexShrink: 0 }} /> {str}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(recData.reasons_for_remark) && recData.reasons_for_remark.length > 0 && (
            <div>
              <strong style={{ fontSize: '0.86rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <AlertTriangle size={15} /> Reasons for Remark / Areas for Improvement:
              </strong>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {recData.reasons_for_remark.map((rmk, idx) => (
                  <div key={idx} style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', padding: '8px 12px', borderRadius: '8px', fontSize: '0.84rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={14} style={{ color: '#f59e0b', flexShrink: 0 }} /> {rmk}
                  </div>
                ))}
              </div>
            </div>
          )}


        </div>
      ) : (
        <div style={{ padding: '20px', background: '#fff', borderRadius: '10px', border: '1px dashed #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <span style={{ fontSize: '0.9rem', color: '#475569' }}>
            Click below to let OpenRouter AI analyze this candidate's resume, skills, and projects for exact placement fit.
          </span>
          <button
            onClick={handleGenerateRecommendation}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)' }}
          >
            <Sparkles size={16} /> Run AI Placement Evaluation
          </button>
        </div>
      )}

      {/* AI Disclaimer Modal */}
      {showDisclaimer && (
        <AiDisclaimerCard
          featureName="AI Placement Recommendation"
          onAgree={() => {
            sessionStorage.setItem('ai_disclaimer_agreed', '1');
            setShowDisclaimer(false);
            if (pendingAction) {
              pendingAction();
              setPendingAction(null);
            }
          }}
          onCancel={() => {
            setShowDisclaimer(false);
            setPendingAction(null);
          }}
        />
      )}
    </div>
  );
};

export default AIPlacementRecommendationCard;
